/**
 * ServicesStep - Select specific services offered
 *
 * Service lists keyed by NAICS sector code.
 * For unmapped sectors, shows generic business services.
 */

import React, { useMemo } from 'react';
import { Stack, Text, Checkbox, Paper, Group, Badge, TextInput, Alert } from '@mantine/core';
import { IconInfoCircle, IconSearch } from '@tabler/icons-react';

export interface ServicesStepProps {
  industry?: string;
  selected: string[];
  onChange: (services: string[]) => void;
}

// Service options by NAICS sector code
const SERVICES_BY_SECTOR: Record<string, Array<{ id: string; label: string }>> = {
  '62': [
    { id: 'personal_care', label: 'Personal Care Services' },
    { id: 'medication_management', label: 'Medication Management' },
    { id: 'skilled_nursing', label: 'Skilled Nursing' },
    { id: 'physical_therapy', label: 'Physical Therapy' },
    { id: 'occupational_therapy', label: 'Occupational Therapy' },
    { id: 'speech_therapy', label: 'Speech Therapy' },
    { id: 'meal_preparation', label: 'Meal Preparation' },
    { id: 'transportation', label: 'Transportation Services' },
    { id: 'respite_care', label: 'Respite Care' },
    { id: 'memory_care', label: 'Memory Care (Dementia/Alzheimer\'s)' },
  ],
  '52': [
    { id: 'banking', label: 'Banking Services' },
    { id: 'lending', label: 'Lending/Mortgages' },
    { id: 'insurance', label: 'Insurance Products' },
    { id: 'investment', label: 'Investment Advisory' },
    { id: 'tax_preparation', label: 'Tax Preparation' },
    { id: 'payroll', label: 'Payroll Processing' },
    { id: 'auditing', label: 'Auditing Services' },
    { id: 'wealth_management', label: 'Wealth Management' },
  ],
  '54': [
    { id: 'legal', label: 'Legal Services' },
    { id: 'accounting', label: 'Accounting/Bookkeeping' },
    { id: 'consulting', label: 'Management Consulting' },
    { id: 'engineering', label: 'Engineering Services' },
    { id: 'architecture', label: 'Architectural Design' },
    { id: 'it_services', label: 'IT/Software Services' },
    { id: 'research', label: 'Research & Development' },
    { id: 'advertising', label: 'Advertising/Marketing' },
  ],
  '23': [
    { id: 'residential', label: 'Residential Construction' },
    { id: 'commercial_build', label: 'Commercial Construction' },
    { id: 'renovation', label: 'Renovation/Remodeling' },
    { id: 'electrical', label: 'Electrical Contracting' },
    { id: 'plumbing', label: 'Plumbing/HVAC' },
    { id: 'concrete', label: 'Concrete/Masonry' },
    { id: 'roofing', label: 'Roofing' },
    { id: 'demolition', label: 'Demolition/Site Work' },
  ],
  '31-33': [
    { id: 'production', label: 'Product Manufacturing' },
    { id: 'assembly', label: 'Assembly/Fabrication' },
    { id: 'packaging', label: 'Packaging/Labeling' },
    { id: 'quality_control', label: 'Quality Control/Testing' },
    { id: 'warehousing', label: 'Warehousing/Distribution' },
    { id: 'custom_manufacturing', label: 'Custom/Contract Manufacturing' },
    { id: 'maintenance', label: 'Equipment Maintenance' },
  ],
  '72': [
    { id: 'dine_in', label: 'Dine-In Service' },
    { id: 'takeout', label: 'Takeout/Carryout' },
    { id: 'delivery', label: 'Delivery Service' },
    { id: 'catering_events', label: 'Event Catering' },
    { id: 'catering_corporate', label: 'Corporate Catering' },
    { id: 'alcohol', label: 'Alcohol Service' },
    { id: 'lodging', label: 'Lodging/Accommodations' },
    { id: 'event_venue', label: 'Event Venue' },
  ],
  '44-45': [
    { id: 'in_store', label: 'In-Store Sales' },
    { id: 'online', label: 'Online/E-commerce Sales' },
    { id: 'consignment', label: 'Consignment Services' },
    { id: 'buy_trade', label: 'Buy/Trade Programs' },
    { id: 'layaway', label: 'Layaway Plans' },
    { id: 'gift_registry', label: 'Gift Registry' },
    { id: 'custom_orders', label: 'Custom Orders' },
    { id: 'delivery', label: 'Delivery Service' },
  ],
  '81': [
    { id: 'self_service', label: 'Self-Service Laundry' },
    { id: 'dry_cleaning', label: 'Dry Cleaning' },
    { id: 'auto_repair', label: 'Auto Repair/Maintenance' },
    { id: 'equipment_repair', label: 'Equipment Repair' },
    { id: 'personal_care_services', label: 'Personal Care (salon, barber)' },
    { id: 'funeral', label: 'Funeral Services' },
    { id: 'pet_care', label: 'Pet Care/Grooming' },
  ],
  '61': [
    { id: 'k12', label: 'K-12 Education' },
    { id: 'higher_ed', label: 'Higher Education' },
    { id: 'vocational', label: 'Vocational/Trade Training' },
    { id: 'tutoring', label: 'Tutoring/Test Prep' },
    { id: 'online_learning', label: 'Online Learning' },
    { id: 'continuing_ed', label: 'Continuing Education' },
  ],
  '48-49': [
    { id: 'freight', label: 'Freight/Cargo' },
    { id: 'passenger', label: 'Passenger Transport' },
    { id: 'last_mile', label: 'Last-Mile Delivery' },
    { id: 'storage', label: 'Storage/Warehousing' },
    { id: 'logistics', label: 'Logistics Management' },
    { id: 'moving', label: 'Moving/Relocation' },
  ],
  '51': [
    { id: 'publishing', label: 'Publishing/Content' },
    { id: 'software', label: 'Software Development' },
    { id: 'data_processing', label: 'Data Processing/Hosting' },
    { id: 'telecom', label: 'Telecommunications' },
    { id: 'streaming', label: 'Streaming/Broadcasting' },
  ],
  '53': [
    { id: 'property_management', label: 'Property Management' },
    { id: 'leasing', label: 'Leasing/Rental' },
    { id: 'real_estate_sales', label: 'Real Estate Sales' },
    { id: 'appraisal', label: 'Appraisal Services' },
  ],
  '11': [
    { id: 'crop_production', label: 'Crop Production' },
    { id: 'livestock', label: 'Livestock/Animal Production' },
    { id: 'forestry', label: 'Forestry/Logging' },
    { id: 'fishing', label: 'Fishing/Aquaculture' },
  ],
  '71': [
    { id: 'live_events', label: 'Live Events/Performances' },
    { id: 'sports', label: 'Sports/Recreation' },
    { id: 'museum', label: 'Museum/Gallery' },
    { id: 'amusement', label: 'Amusement/Theme Park' },
    { id: 'gambling', label: 'Gambling/Casino' },
  ],
};

// Generic services for NAICS sectors without specific mapping
const GENERIC_SERVICES: Array<{ id: string; label: string }> = [
  { id: 'customer_service', label: 'Customer Service' },
  { id: 'sales', label: 'Sales' },
  { id: 'billing', label: 'Billing/Invoicing' },
  { id: 'scheduling', label: 'Scheduling/Appointments' },
  { id: 'inventory', label: 'Inventory Management' },
  { id: 'reporting', label: 'Reporting/Analytics' },
  { id: 'compliance', label: 'Compliance/Regulatory' },
  { id: 'hr', label: 'Human Resources' },
];

export function ServicesStep({ industry, selected, onChange }: ServicesStepProps) {
  const [searchQuery, setSearchQuery] = React.useState('');

  const services = useMemo(() => {
    if (!industry) return [];
    return SERVICES_BY_SECTOR[industry] ?? GENERIC_SERVICES;
  }, [industry]);

  const filteredServices = useMemo(() => {
    if (!searchQuery) return services;
    const query = searchQuery.toLowerCase();
    return services.filter((s) => s.label.toLowerCase().includes(query));
  }, [services, searchQuery]);

  const handleToggle = (serviceId: string, checked: boolean) => {
    if (checked) {
      onChange([...selected, serviceId]);
    } else {
      onChange(selected.filter((s) => s !== serviceId));
    }
  };

  if (!industry) {
    return (
      <Alert icon={<IconInfoCircle size={16} />} color="yellow">
        Please select an industry first.
      </Alert>
    );
  }

  return (
    <Stack gap="lg" py="md">
      <Group justify="space-between">
        <Text size="lg" fw={500}>
          What services will you offer?
        </Text>
        {selected.length > 0 && (
          <Badge size="lg" variant="light">
            {selected.length} selected
          </Badge>
        )}
      </Group>

      <TextInput
        placeholder="Search services..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.currentTarget.value)}
        leftSection={<IconSearch size={16} />}
      />

      <Stack gap="xs">
        {filteredServices.map((service) => {
          const isSelected = selected.includes(service.id);

          return (
            <Paper
              key={service.id}
              p="sm"
              withBorder
              style={{
                cursor: 'pointer',
                borderColor: isSelected ? 'var(--mantine-color-blue-6)' : undefined,
              }}
              onClick={() => handleToggle(service.id, !isSelected)}
            >
              <Group>
                <Checkbox checked={isSelected} onChange={() => {}} />
                <Text size="sm">{service.label}</Text>
              </Group>
            </Paper>
          );
        })}
      </Stack>

      <Text size="sm" c="dimmed">
        Select the services you plan to offer. This helps the LLM composition engine
        generate more targeted workflows and compliance rules. You can modify this later.
      </Text>
    </Stack>
  );
}

export default ServicesStep;
